import type { RepairProposal } from "../repair/types";

import type {
  DepartmentId,
  WorkAuthority,
  WorkPackage,
} from "./agentExecutionContract";

function inferDepartment(proposal: RepairProposal): DepartmentId {
  const evidence = [
    ...proposal.affectedPaths,
    ...proposal.requiredCapabilities,
    ...proposal.verification.map((item) => item.type),
  ]
    .join(" ")
    .toLowerCase();

  if (
    evidence.includes("security") ||
    evidence.includes("rls") ||
    evidence.includes("auth")
  ) {
    return "SECURITY";
  }

  if (
    evidence.includes("database") ||
    evidence.includes("supabase") ||
    evidence.includes("migration")
  ) {
    return "DATABASE";
  }

  if (
    evidence.includes("cloudflare") ||
    evidence.includes("infrastructure") ||
    evidence.includes("deploy")
  ) {
    return "INFRASTRUCTURE";
  }

  if (proposal.verification.some((item) => item.type === "browser")) {
    return "QA";
  }

  if (evidence.includes("docs") || evidence.includes("spec")) {
    return "DOCUMENTATION";
  }

  return "ENGINEERING";
}

function safeAuthorities(): readonly WorkAuthority[] {
  return ["READ", "WORKSPACE_WRITE", "TEST_EXECUTION", "GIT_LOCAL"];
}

export function repairProposalToWorkPackage(
  proposal: RepairProposal,
): WorkPackage {
  const verification = proposal.verification
    .filter((item) => item.required)
    .map((item) => `${item.type}: ${item.commandOrMethod}`);

  return {
    id: proposal.id,

    phaseId: `repair:${proposal.severity}`,

    department: inferDepartment(proposal),

    agentRole: proposal.preferredWorkers[0] ?? "SPECIALIST",

    objective: proposal.objective,

    instructions: [
      proposal.title,

      `Rollback: ${proposal.rollbackStrategy}`,

      ...proposal.evidencePaths.map((path) => `Inspect evidence: ${path}`),
    ],

    dependencies: proposal.dependencies,

    blocks: [],

    resourceScope: {
      paths: proposal.affectedPaths,

      services: proposal.requiredCapabilities,

      integrations: [],
    },

    authorities: safeAuthorities(),

    definitionOfDone: verification.length
      ? verification
      : [
          "Target finding is resolved.",
          "Relevant verification passes.",
          "No unrelated regression is introduced.",
        ],

    mayRunInParallel: true,
  };
}
